import { defineStore } from 'pinia'
import type { PayForwardMiniProduct } from '@/types/model-types.ts'
import { fbApi, FbApiError } from '@/utils/http-api.ts'

/**
 * This is really "store" but useStoreStore seems silly, so went with "shop".
 * The backend APIs are store endpoints.
 */

type ShopState = {
  cartItemCount: number
  payForward: {
    loading: boolean
    error: string
    products: PayForwardMiniProduct[]
  }
}

export const useShopStore = defineStore('shop', {
  state: (): ShopState => ({
    cartItemCount: 0,
    payForward: {
      loading: false,
      error: '',
      products: [],
    },
  }),

  getters: {
    payForwardProducts(state: ShopState): PayForwardMiniProduct[] {
      return state.payForward.products
    },
  },

  actions: {
    setCartItemCount(itemCount: number): void {
      this.cartItemCount = itemCount
    },

    /**
     * Fetch and cache, do not reload after setting.
     */
    async initPayForwardProducts(): Promise<void> {
      if (this.payForward.loading || this.payForward.products.length > 0) {
        return
      }

      this.payForward.loading = true
      this.payForward.error = ''

      try {
        const res = await fbApi.get('/store/pay-forward', {
          skipErrorModal: true,
        })

        // missing data, shouldn't happen
        if (!res || !res.body || !res.body.data) {
          throw new Error(`Missing response data`)
        }

        this.payForward.products = res.body.data
      } catch (e: unknown) {
        this.payForward.error =
          e && e instanceof FbApiError
            ? 'Unable to load pay it forward products'
            : 'Unknown error attempting to load pay it forward products'
      } finally {
        this.payForward.loading = false
      }
    },
  },
})
